/**
 * DELE 考试特定的题目类型定义
 */

import {
  QuestionType,
  createExamQuestionTypeDefinition,
  createFieldDefinition,
  type ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';

/**
 * DELE 等级合法取值（基于 CEFR 标准）
 */
const DELE_VALID_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/**
 * DELE 等级字段（各 Section 共用）
 */
function createDeleLevelField() {
  return createFieldDefinition(
    'deleLevel',
    'string',
    true,
    '对应的 DELE 等级',
    {
      validator: (value) => {
        return DELE_VALID_LEVELS.includes(value as string)
          ? true
          : `不支持的 DELE 等级: ${value}，必须是以下之一: ${DELE_VALID_LEVELS.join(', ')}`;
      },
    }
  );
}

/**
 * DELE 大题编号字段（各 Section 共用，大题数随等级变化，这里仅做下限校验）
 */
function createTaskNumberField() {
  return createFieldDefinition(
    'taskNumber',
    'number',
    true,
    '大题编号 (Tarea)',
    {
      validator: (value) => {
        const num = Number(value);
        if (Number.isNaN(num) || num < 1) {
          return 'DELE 大题编号必须是不小于 1 的数字';
        }
        return true;
      },
    }
  );
}

/**
 * DELE 阅读题定义
 */
export const deleReadingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'dele_reading',
    QuestionType.SINGLE_CHOICE,
    'DELE 阅读理解题',
    {
      requiredStimulus: ['passage'],
      extraFields: {
        // DELE 等级
        deleLevel: createDeleLevelField(),
        // 大题编号
        taskNumber: createTaskNumberField(),
        // 阅读材料类型
        textType: createFieldDefinition(
          'textType',
          'string',
          false,
          '阅读材料类型（如文章、广告、通知、电子邮件等）'
        ),
      },

      constraints: {
        // 阅读题数随等级变化（A1 约 20 题至 C2 约 40 题），不设固定上限
      },

      customValidator: (question) => {
        const errors = [];

        // 验证阅读材料长度
        if (question.passage) {
          const wordCount = question.passage.split(/\s+/).length;
          if (wordCount > 1500) {
            errors.push({
              field: 'passage',
              message: 'DELE 阅读材料不能超过 1500 词',
              code: 'DELE_PASSAGE_TOO_LONG',
            });
          }
        }

        // 验证大题编号
        const taskNumber = Number(question.taskNumber);
        if (!Number.isNaN(taskNumber) && taskNumber < 1) {
          errors.push({
            field: 'taskNumber',
            message: 'DELE 阅读大题编号必须不小于 1',
            code: 'DELE_INVALID_TASK_NUMBER',
          });
        }

        return errors;
      },
    }
  );

/**
 * DELE 听力题定义
 */
export const deleListeningDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'dele_listening',
    QuestionType.SINGLE_CHOICE,
    'DELE 听力理解题',
    {
      requiredStimulus: ['audio'],
      extraFields: {
        // DELE 等级
        deleLevel: createDeleLevelField(),
        // 大题编号
        taskNumber: createTaskNumberField(),
        // 音频时长（秒）
        audioDuration: createFieldDefinition(
          'audioDuration',
          'number',
          false,
          '音频时长（秒）'
        ),
      },

      constraints: {
        maxAudioDuration: 300, // DELE 听力材料一般不超过 5 分钟
        supportedFormats: ['mp3', 'wav'],
        requiresTranscript: true, // DELE 练习提供原文
      },

      customValidator: (question) => {
        const errors = [];

        // DELE 听力音频时长验证
        if (question.audioDuration) {
          if (question.audioDuration > 300) {
            errors.push({
              field: 'audioDuration',
              message: 'DELE 听力音频不能超过 5 分钟',
              code: 'DELE_AUDIO_TOO_LONG',
            });
          }
          if (question.audioDuration < 15) {
            errors.push({
              field: 'audioDuration',
              message: 'DELE 听力音频不能短于 15 秒',
              code: 'DELE_AUDIO_TOO_SHORT',
            });
          }
        }

        // 验证大题编号
        const taskNumber = Number(question.taskNumber);
        if (!Number.isNaN(taskNumber) && taskNumber < 1) {
          errors.push({
            field: 'taskNumber',
            message: 'DELE 听力大题编号必须不小于 1',
            code: 'DELE_INVALID_TASK_NUMBER',
          });
        }

        return errors;
      },
    }
  );

/**
 * DELE 写作题定义
 */
export const deleWritingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'dele_writing',
    QuestionType.FILL_BLANK, // 写作为开放式作答，用填空类型承载
    'DELE 书面表达题',
    {
      responseMode: 'open_ended',
      extraFields: {
        // DELE 等级
        deleLevel: createDeleLevelField(),
        // 大题编号
        taskNumber: createTaskNumberField(),
        // 最低字数要求（随等级变化，如 A1 约 50-100 词，C2 约 250 词以上）
        minWordCount: createFieldDefinition(
          'minWordCount',
          'number',
          false,
          '最低字数要求'
        ),
      },

      constraints: {
        // 写作大题数随等级变化（通常 2-4 个 Tarea），不设固定上限
      },

      customValidator: (question) => {
        const errors = [];

        // 验证大题编号
        const taskNumber = Number(question.taskNumber);
        if (!Number.isNaN(taskNumber) && taskNumber < 1) {
          errors.push({
            field: 'taskNumber',
            message: 'DELE 写作大题编号必须不小于 1',
            code: 'DELE_INVALID_TASK_NUMBER',
          });
        }

        // 验证最低字数
        const minWordCount = Number(question.minWordCount);
        if (question.minWordCount != null && !Number.isNaN(minWordCount)) {
          if (minWordCount < 10) {
            errors.push({
              field: 'minWordCount',
              message: 'DELE 写作最低字数要求不能低于 10 词',
              code: 'DELE_WRITING_MIN_WORDS_TOO_LOW',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * 所有 DELE 题目类型定义
 */
export const DELE_QUESTION_TYPE_DEFINITIONS: Record<
  string,
  ExamSpecificQuestionTypeDefinition
> = {
  [deleReadingDefinition.id]: deleReadingDefinition,
  [deleListeningDefinition.id]: deleListeningDefinition,
  [deleWritingDefinition.id]: deleWritingDefinition,
};

/**
 * 根据基础类型获取 DELE 题目类型定义
 */
export function getDELEQuestionTypeDefinition(
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  return Object.values(DELE_QUESTION_TYPE_DEFINITIONS).find(
    (def) => def.baseType === baseType
  );
}

/**
 * 获取 DELE 题目类型的 Section 建议
 * 根据题目类型推荐应该属于哪个 Section
 */
export function getDELESectionSuggestion(
  questionTypeId: string
): 'reading' | 'listening' | 'writing' | null {
  const map: Record<string, 'reading' | 'listening' | 'writing'> = {
    [deleReadingDefinition.id]: 'reading',
    [deleListeningDefinition.id]: 'listening',
    [deleWritingDefinition.id]: 'writing',
  };
  return map[questionTypeId] ?? null;
}
